import React from 'react';
import styles from './table.module.scss';
import SortIndicator, { sortIndicatorClasses } from '../sortIndicator/SortIndicator';

export interface TableColumn<T = any> {
  key: string;
  label: string | React.ReactNode;
  width?: string | number;
  align?: 'left' | 'center' | 'right';
  render?: (value: any, row: T, index: number) => React.ReactNode;
  headerAlign?: 'left' | 'center' | 'right';
  className?: string;
  headerClassName?: string;
  /** Opt-out of header-click sorting. Defaults to true. Set false on action columns or
   *  cells that don't have a meaningful comparable value. */
  sortable?: boolean;
}

export interface TableProps<T = any> {
  columns: TableColumn<T>[];
  data: T[];
  keyExtractor?: (row: T, index: number) => string | number;
  emptyMessage?: string;
  emptyState?: React.ReactNode;
  onRowClick?: (row: T, index: number) => void;
  rowClassName?: (row: T, index: number) => string;
  stickyHeader?: boolean;
  minWidth?: string | number;
  className?: string;
  containerClassName?: string;
  showHeader?: boolean;
  maxHeight?: string | number;
  /** When true, use page-level scrolling instead of container-level scrolling. */
  disableContainerScroll?: boolean
  /** Fill the parent height and scroll inside the table (list pages). */
  fillHeight?: boolean;
  /** Currently-sorted column key. `null` = no column sort. */
  sortKey?: string | null;
  /** Sort direction for the active column. `null` = column not sorted. */
  sortDirection?: 'asc' | 'desc' | null;
  /** Click handler for sortable headers. Receives the column key. Page owns the 3-state cycle. */
  onSort?: (key: string) => void;
}

const Table = <T extends Record<string, any> = Record<string, any>>({
  columns,
  data,
  keyExtractor,
  emptyMessage = 'No data available',
  emptyState,
  onRowClick,
  rowClassName,
  stickyHeader = true,
  minWidth,
  className,
  containerClassName,
  showHeader = true,
  maxHeight,
  disableContainerScroll = false,
  fillHeight = false,
  sortKey = null,
  sortDirection = null,
  onSort,
}: TableProps<T>) => {
  // A column is sortable when caller wired up onSort, AND the column didn't opt out,
  // AND it's not an obviously non-sortable action column (the convention is key="actions").
  const isColumnSortable = (column: TableColumn<T>): boolean => {
    if (!onSort) return false;
    if (column.sortable === false) return false;
    if (column.key === 'actions') return false;
    return true;
  };
  const getRowKey = (row: T, index: number): string | number => {
    if (keyExtractor) {
      return keyExtractor(row, index);
    }
    // Try to find common id fields
    if ('id' in row && row.id != null) return row.id;
    if ('_id' in row && row._id != null) return row._id;
    return index;
  };

  const getColumnWidth = (column: TableColumn<T>): string | undefined => {
    if (column.width) {
      return typeof column.width === 'number' ? `${column.width}px` : column.width;
    }
    return undefined;
  };

  const getMaxHeight = (): React.CSSProperties | undefined => {
    if (maxHeight) {
      return {
        maxHeight: typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight,
      };
    }
    return undefined;
  };

  if (data.length === 0) {
    console.log("emptyMessage", emptyMessage,emptyState);
    // return (
    //   <div 
    //     className={`${styles.tableContainer} ${containerClassName || ''}`}
    //     style={getMaxHeight()}
    //   >
    //     {emptyState || (
    //       <div className={styles.emptyState}>
    //         <p>{emptyMessage}</p>
    //       </div>
    //     )}
    //   </div>
    // );
  }

  return (
    <div 
      className={`${styles.tableContainer} ${disableContainerScroll ? styles.tableContainerNoScroll : ''} ${fillHeight ? styles.tableContainerFillHeight : ''} ${containerClassName || ''}`}
      style={getMaxHeight()}
    >
      <table
        className={`${styles.table} ${className || ''}`}
        style={minWidth ? { minWidth: typeof minWidth === 'number' ? `${minWidth}px` : minWidth } : undefined}
      >
        {showHeader && (
          <thead
            style={stickyHeader ? { position: 'sticky', top: 0, zIndex: 10 } : undefined}
          >
            <tr>
              {columns.map((column) => {
                const sortable = isColumnSortable(column);
                const isActiveSort = sortable && sortKey === column.key && sortDirection != null;
                const indicatorDirection: 'asc' | 'desc' | null = isActiveSort ? sortDirection : null;
                const thClassName = [
                  column.headerClassName || '',
                  sortable ? sortIndicatorClasses.sortable : '',
                  isActiveSort ? sortIndicatorClasses.active : '',
                ].filter(Boolean).join(' ') || undefined;
                return (
                  <th
                    key={column.key}
                    className={thClassName}
                    style={{
                      width: getColumnWidth(column),
                      textAlign: column.headerAlign || column.align || 'left',
                    }}
                    onClick={sortable ? () => onSort && onSort(column.key) : undefined}
                    aria-sort={
                      isActiveSort
                        ? (sortDirection === 'asc' ? 'ascending' : 'descending')
                        : sortable ? 'none' : undefined
                    }
                  >
                    {column.label}
                    {sortable && <SortIndicator direction={indicatorDirection} />}
                  </th>
                );
              })}
            </tr>
          </thead>
        )}
        <tbody>
          {data.map((row, rowIndex) => {
            const key = getRowKey(row, rowIndex);
            const customRowClassName = rowClassName ? rowClassName(row, rowIndex) : '';
            return (
              <tr
                key={key}
                className={customRowClassName}
                onClick={onRowClick ? () => onRowClick(row, rowIndex) : undefined}
                style={onRowClick ? { cursor: 'pointer' } : undefined}
              >
                {columns.map((column) => {
                  const value = row[column.key];
                  const cellContent = column.render
                    ? column.render(value, row, rowIndex)
                    : value != null
                    ? String(value)
                    : '';

                  return (
                    <td
                      key={column.key}
                      className={column.className}
                      style={{
                        textAlign: column.align || 'left',
                      }}
                    >
                      {cellContent}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default Table;
